import { CROPS } from "./catalog";
import { REGIONS } from "./regions";
import { RESOURCES } from "./resources";
import { GameArt } from "./art";
import { Landscape } from "./landscape";
import { STATUS_RULES } from "./status";
import { displayMetrics } from "./motion";
import { inkAsset, inkPart } from "./ink-assets";
import { ExpeditionPainter } from "./expedition-renderer";
import { bossDrawSize } from "./battle-framing";
import { ENEMIES } from "./enemies";
import { clamp, hashNoise, lerp } from "./math";
import { CAMP, GATE, WELL, worldScale } from "./rules";
import type { Bolt, CombatEffect, GardenPlot, Threat, World } from "./world";
import type { Rect } from "./layout";
import type {
  Companion,
  Enemy,
  FarmPlot,
  FloatingText,
  Particle,
  Player,
  ResourceNode,
  Vec2,
  Weather,
} from "./types";

export const VIEW_WIDTH = 1600;
export const VIEW_HEIGHT = 900;

export interface RenderSnapshot {
  compactCombat?: boolean;
  scene?: World["scene"];
  run?: World["run"];
  attackMode?: "seed" | "crescent";
  time: number;
  dayTime: number;
  weather: Weather;
  camera: Vec2;
  cameraShake: number;
  pointerWorld: Vec2;
  player: Player;
  plots: FarmPlot[];
  companions: Companion[];
  enemies: Enemy[];
  resourceNodes: ResourceNode[];
  particles: Particle[];
  floatingTexts: FloatingText[];
  selectedPlotId: number | null;
  selectedCompanionId: number | null;
  possessedCompanionId: number | null;
  threats: Threat[];
  bolts: Bolt[];
  destination: Vec2 | null;
  effects: CombatEffect[];
  ghosts?: World["ghosts"];
}

export class Renderer {
  private readonly legacy = new URLSearchParams(location.search).has("legacy");
  private readonly art = new GameArt(!this.legacy);
  private readonly landscape = new Landscape();
  private readonly expeditionArt = new ExpeditionPainter();
  private readonly context: CanvasRenderingContext2D;
  private cssWidth = 1;
  private cssHeight = 1;
  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;
  private dpr = 1;
  private viewport: Rect | null = null;
  private compactCombat = false;

  setViewport(rect: Rect): void {
    if (JSON.stringify(this.viewport) === JSON.stringify(rect)) return;
    this.viewport = rect;
    this.resize();
  }

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("当前浏览器不支持 Canvas 2D。");
    this.context = context;
    this.resize();
  }

  resize(): void {
    this.cssWidth = window.innerWidth;
    this.cssHeight = window.innerHeight;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(this.cssWidth * this.dpr);
    this.canvas.height = Math.round(this.cssHeight * this.dpr);
    const view = this.viewport ?? {
      x: 0,
      y: 0,
      width: this.cssWidth,
      height: this.cssHeight,
    };
    this.scale = worldScale(view.width, view.height);
    this.offsetX = view.x + (view.width - VIEW_WIDTH * this.scale) * 0.5;
    this.offsetY = view.y + (view.height - VIEW_HEIGHT * this.scale) * 0.5;
  }

  screenToWorld(clientX: number, clientY: number, camera: Vec2): Vec2 {
    const logicalX = (clientX - this.offsetX) / this.scale;
    const logicalY = (clientY - this.offsetY) / this.scale;
    return {
      x: logicalX - VIEW_WIDTH * 0.5 + camera.x,
      y: logicalY - VIEW_HEIGHT * 0.5 + camera.y,
    };
  }
  containsScreen(x: number, y: number): boolean {
    const v = this.viewport;
    return (
      !v || (x >= v.x && y >= v.y && x <= v.x + v.width && y <= v.y + v.height)
    );
  }

  draw(snapshot: RenderSnapshot): void {
    this.compactCombat = snapshot.compactCombat ?? false;
    const ctx = this.context;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.cssWidth, this.cssHeight);
    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    ctx.save();
    // 背景铺满屏幕，世界保持可点击的比例；相机负责展示屏幕外的灵田。
    ctx.translate(-this.offsetX / this.scale, -this.offsetY / this.scale);
    ctx.scale(
      this.cssWidth / (VIEW_WIDTH * this.scale),
      this.cssHeight / (VIEW_HEIGHT * this.scale),
    );
    this.drawSky(snapshot);
    ctx.restore();

    const shakeX =
      snapshot.cameraShake > 0
        ? (Math.random() - 0.5) * snapshot.cameraShake
        : 0;
    const shakeY =
      snapshot.cameraShake > 0
        ? (Math.random() - 0.5) * snapshot.cameraShake
        : 0;
    ctx.save();
    if (this.viewport) {
      const v = this.viewport;
      ctx.beginPath();
      ctx.rect(
        (v.x - this.offsetX) / this.scale,
        (v.y - this.offsetY) / this.scale,
        v.width / this.scale,
        v.height / this.scale,
      );
      ctx.clip();
    }
    ctx.translate(
      VIEW_WIDTH * 0.5 - snapshot.camera.x + shakeX,
      VIEW_HEIGHT * 0.5 - snapshot.camera.y + shakeY,
    );
    if (!this.legacy) {
      this.landscape.draw(
        ctx,
        snapshot.time,
        snapshot.camera,
        snapshot.run?.mapId ?? "garden",
      );
      if (snapshot.run)
        this.expeditionArt.landmarks(
          ctx,
          snapshot.run,
          snapshot.time,
          snapshot.destination,
        );
      else this.drawLandmarks(snapshot);
    } else if (snapshot.scene === "expedition" && snapshot.run) {
      this.expeditionArt.terrainLayer(
        ctx,
        snapshot.run,
        snapshot.time,
        snapshot.camera,
      );
      this.expeditionArt.landmarks(
        ctx,
        snapshot.run,
        snapshot.time,
        snapshot.destination,
      );
    } else {
      if (this.art.ready(this.art.island)) {
        // 原画草地平面映射到既有椭圆碰撞区域，玩法坐标与存档位置保持不变。
        ctx.drawImage(this.art.island, 170, 25, 1260, 1120);
      } else {
        this.drawDistantIslands(snapshot.time);
        this.drawIsland(snapshot.time, snapshot.dayTime);
        this.drawWaterGarden(snapshot.time);
      }
      this.drawFrontier(snapshot.time, snapshot.camera);
      this.drawLandmarks(snapshot);
      this.drawGroundDetails(snapshot.time);
    }
    for (const plot of snapshot.plots)
      this.drawPlot(
        plot,
        snapshot.time,
        plot.id === snapshot.selectedPlotId,
        Math.hypot(
          plot.pos.x - snapshot.player.pos.x,
          plot.pos.y - snapshot.player.pos.y,
        ) < 140,
      );
    this.drawTelegraphs(snapshot);

    const orderedActors: Array<{ y: number; draw: () => void }> = [];
    for (const node of snapshot.resourceNodes)
      orderedActors.push({
        y: node.pos.y,
        draw: () =>
          this.drawResourceNode(
            node,
            snapshot.time,
            Math.hypot(
              node.pos.x - snapshot.player.pos.x,
              node.pos.y - snapshot.player.pos.y,
            ) < 150,
          ),
      });
    orderedActors.push({
      y: snapshot.player.pos.y,
      draw: () =>
        this.drawPlayer(
          snapshot.player,
          snapshot.time,
          snapshot.possessedCompanionId !== null,
        ),
    });
    for (const companion of snapshot.companions) {
      orderedActors.push({
        y: companion.pos.y,
        draw: () =>
          this.drawCompanion(
            companion,
            snapshot.time,
            companion.id === snapshot.selectedCompanionId,
          ),
      });
    }
    for (const enemy of snapshot.enemies) {
      orderedActors.push({
        y: enemy.pos.y,
        draw: () => this.drawEnemy(enemy, snapshot.time),
      });
    }
    orderedActors.sort((a, b) => a.y - b.y);
    for (const actor of orderedActors) actor.draw();
    for (const actor of [
      snapshot.player,
      ...snapshot.companions,
      ...snapshot.enemies,
    ])
      if (actor.statuses?.length) {
        ctx.font = "600 12px sans-serif";
        ctx.textAlign = "center";
        actor.statuses.forEach((s, i) => {
          ctx.fillStyle = STATUS_RULES[s.id].color;
          ctx.fillText(
            `${STATUS_RULES[s.id].name} ${s.remaining.toFixed(1)}`,
            actor.pos.x,
            actor.pos.y - 114 - i * 15,
          );
        });
      }

    for (const bolt of snapshot.bolts) {
      ctx.save();
      ctx.translate(bolt.pos.x, bolt.pos.y - 14);
      ctx.rotate(Math.atan2(bolt.velocity.y, bolt.velocity.x));
      const lance = bolt.kind === "lance";
      ctx.fillStyle = lance ? "#ffeab0" : bolt.friendly ? "#adffe3" : "#e69fff";
      ctx.shadowColor = ctx.fillStyle;
      ctx.shadowBlur = lance ? 25 : 14;
      const trail = ctx.createLinearGradient(-55, 0, 8, 0);
      trail.addColorStop(0, "transparent");
      trail.addColorStop(1, ctx.fillStyle);
      ctx.strokeStyle = trail;
      ctx.lineWidth = lance ? 13 : 5;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(-55, 0);
      ctx.lineTo(0, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(0, 0, lance ? 20 : 9, lance ? 6 : 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fffbea";
      ctx.beginPath();
      ctx.arc(2, 0, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    for (const effect of snapshot.effects) this.drawEffect(effect);
    for (const ghost of snapshot.ghosts ?? []) {
      ctx.save();
      ctx.translate(ghost.pos.x, ghost.pos.y);
      if (
        (ghost.art === "thorn-warden" || ghost.art === "ember-tortoise") &&
        this.legacy
      ) {
        this.expeditionArt.boss(
          ctx,
          ghost.art,
          {
            animation: {
              clip: ghost.clip,
              age: ghost.age,
              duration: ghost.duration,
            },
          },
          snapshot.time,
          ghost.facing,
          bossDrawSize(this.compactCombat),
        );
        ctx.restore();
        continue;
      }
      this.art.animated(
        ctx,
        ghost.art,
        {
          animation: {
            clip: ghost.clip,
            age: ghost.age,
            duration: ghost.duration,
          },
        },
        snapshot.time,
        ghost.art === "tree"
          ? 160
          : ghost.art === "thorn-warden" || ghost.art === "ember-tortoise"
            ? bossDrawSize(this.compactCombat)
            : 112,
        undefined,
        Math.cos(ghost.facing) < 0,
        ghost.clip === "harvest",
      );
      ctx.restore();
    }

    for (const particle of snapshot.particles) this.drawParticle(particle);
    for (const floatingText of snapshot.floatingTexts)
      this.drawFloatingText(floatingText);
    this.drawPointer(snapshot.pointerWorld, snapshot.time);
    ctx.restore();

    this.drawWeather(snapshot);
    // 地点与任务由DOM安全区呈现，不再在画布顶部重复覆盖玩家视野。
    ctx.restore();
  }

  private drawFrontier(time: number, camera: Vec2): void {
    const ctx = this.context;
    for (const r of REGIONS.slice(1)) {
      if (
        Math.abs(r.x - camera.x) >
        r.rx + (this.cssWidth / this.scale) * 0.5 + 60
      )
        continue;
      ctx.save();
      const cliff = ctx.createLinearGradient(0, r.y, 0, r.y + 450);
      cliff.addColorStop(0, "#676559");
      cliff.addColorStop(1, "#243c42");
      ctx.fillStyle = cliff;
      ctx.beginPath();
      ctx.ellipse(r.x, r.y + 90, r.rx, r.ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = r.color;
      ctx.beginPath();
      ctx.ellipse(r.x, r.y, r.rx, r.ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.clip();
      if (this.art.ready(this.art.island)) {
        // 只复用原画的草坪纹理，天空和岛下瀑布不盖住邻接的可走区域。
        ctx.globalAlpha = 0.38;
        ctx.drawImage(
          this.art.island,
          220,
          80,
          1050,
          430,
          r.x - r.rx,
          r.y - r.ry,
          r.rx * 2,
          r.ry * 2,
        );
        ctx.globalAlpha = 1;
      }
      for (let i = 0; i < 62; i++) {
        const x = r.x + (hashNoise(i * 2.87) - 0.5) * r.rx * 2,
          y = r.y + (hashNoise(i * 5.31) - 0.5) * r.ry * 2;
        ctx.save();
        ctx.translate(x, y);
        if (i % 4 === 0)
          this.art.animated(ctx, "herb", {}, time + i, 30, 34, false, true);
        else {
          const wind = Math.sin((Math.floor(time * 8 + i) / 8) * 2) * 3;
          ctx.strokeStyle = i % 2 ? "#bdcc87" : "#456c51";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.quadraticCurveTo(wind, -6, wind + 4, -11);
          ctx.stroke();
        }
        ctx.restore();
      }
      ctx.restore();
      ctx.save();
      ctx.fillStyle = "rgba(24,43,37,.72)";
      ctx.beginPath();
      ctx.roundRect(r.x - 110, r.y + 235, 220, 35, 15);
      ctx.fill();
      ctx.fillStyle = "#eee4bd";
      ctx.textAlign = "center";
      ctx.font = '600 17px "Microsoft YaHei UI"';
      ctx.fillText(r.name, r.x, r.y + 258);
      ctx.restore();
    }
    // 两条接壤小径位于碰撞区域交集，画面和导航共享同一入口坐标。
    for (const x of [350, 1250]) {
      ctx.strokeStyle = "#b2ad78";
      ctx.lineWidth = 52;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(x - 58, 485);
      ctx.lineTo(x + 58, 485);
      ctx.stroke();
      ctx.strokeStyle = "#dfc995";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 14]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  private drawLandmarks(snapshot: RenderSnapshot): void {
    const ctx = this.context;
    for (const [pos, title, color] of [
      [WELL, "灵泉 · 免费取水", "#a8dfd6"],
      [CAMP, "营地 · 行囊工坊", "#f0d395"],
      [GATE, "星门 · 远征地图", "#c4ade9"],
    ] as const) {
      ctx.save();
      ctx.translate(pos.x, pos.y);
      ctx.fillStyle = "rgba(18,35,35,.3)";
      ctx.beginPath();
      ctx.ellipse(0, 9, 35, 14, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 4;
      if (
        (pos !== CAMP || !this.legacy) &&
        this.art.animated(
          ctx,
          pos === GATE ? "gate" : pos === CAMP ? "camp" : "well",
          {},
          snapshot.time,
          pos === GATE ? 114 : 94,
          pos === GATE ? 132 : 94,
          false,
          true,
        )
      ) {
        // 地标美术不改变取水 / 挑战命中坐标。
      } else if (pos === GATE) {
        ctx.beginPath();
        ctx.arc(0, -26, 28, Math.PI, 0);
        ctx.lineTo(28, 6);
        ctx.moveTo(-28, -26);
        ctx.lineTo(-28, 6);
        ctx.stroke();
        ctx.fillStyle = "rgba(155,123,196,.25)";
        ctx.beginPath();
        ctx.ellipse(0, -15, 21, 30, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (pos === CAMP) {
        ctx.fillStyle = "#9b9e6d";
        ctx.beginPath();
        ctx.moveTo(-34, 6);
        ctx.lineTo(0, -42);
        ctx.lineTo(34, 6);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = "#31493c";
        ctx.beginPath();
        ctx.moveTo(-10, 6);
        ctx.lineTo(0, -18);
        ctx.lineTo(10, 6);
        ctx.fill();
      } else {
        ctx.fillStyle = "#66978d";
        ctx.beginPath();
        ctx.ellipse(0, -6, 28, 16, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      ctx.font = '600 13px "Microsoft YaHei UI",sans-serif';
      ctx.textAlign = "center";
      ctx.fillStyle = this.legacy ? color : "#4b5037";
      ctx.shadowColor = this.legacy ? "#192d2a" : "#fff4d8";
      ctx.shadowBlur = 3;
      ctx.fillText(title, 0, 34);
      ctx.restore();
    }
    if (snapshot.destination) {
      const p = snapshot.destination;
      ctx.save();
      ctx.setLineDash([6, 7]);
      ctx.strokeStyle = "rgba(239,230,159,.65)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, 25, 13, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawTelegraphs(snapshot: RenderSnapshot): void {
    const ctx = this.context;
    for (const t of snapshot.threats) {
      ctx.save();
      if (t.kind === "ring") {
        // evenodd挖空的内圈是真实安全区，不能用半透明实心圆误导闪避。
        const inner = t.innerRadius ?? 0;
        const progress = 1 - t.remaining / t.duration;
        ctx.fillStyle = `rgba(246,122,79,${0.15 + progress * 0.18})`;
        ctx.beginPath();
        ctx.arc(t.pos.x, t.pos.y, t.radius, 0, Math.PI * 2);
        ctx.arc(t.pos.x, t.pos.y, inner, 0, Math.PI * 2, true);
        ctx.fill("evenodd");
        ctx.strokeStyle = "#ffbf96";
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.strokeStyle = "#bcffe0";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(t.pos.x, t.pos.y, inner, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = "#ffe3bb";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(
          t.pos.x,
          t.pos.y,
          t.radius,
          -Math.PI / 2,
          -Math.PI / 2 + progress * Math.PI * 2,
        );
        ctx.stroke();
        ctx.font = '600 14px "Microsoft YaHei UI",sans-serif';
        ctx.textAlign = "center";
        ctx.fillStyle = "#c2ffe1";
        ctx.fillText("内圈安全", t.pos.x, t.pos.y + inner * 0.6);
        ctx.restore();
        continue;
      }
      if (t.kind === "volley") {
        const angle = Math.atan2(t.pos.y - t.source.y, t.pos.x - t.source.x);
        ctx.strokeStyle = "rgba(232,170,255,.65)";
        ctx.lineWidth = 2;
        ctx.setLineDash([7, 9]);
        for (const spread of [-0.24, 0, 0.24]) {
          ctx.beginPath();
          ctx.moveTo(t.source.x, t.source.y);
          ctx.lineTo(
            t.source.x + Math.cos(angle + spread) * 480,
            t.source.y + Math.sin(angle + spread) * 480,
          );
          ctx.stroke();
        }
        ctx.restore();
        continue;
      }
      if (t.kind === "rush") {
        ctx.lineWidth = t.radius * 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "rgba(240,92,84,.23)";
        ctx.beginPath();
        ctx.moveTo(t.source.x, t.source.y);
        ctx.lineTo(t.pos.x, t.pos.y);
        ctx.stroke();
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#ffb0a0";
        ctx.setLineDash([8, 7]);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.fillStyle =
        t.kind === "heal"
          ? "rgba(168,191,255,.14)"
          : `rgba(234,99,88,${0.12 + (1 - t.remaining / t.duration) * 0.2})`;
      ctx.strokeStyle = t.kind === "heal" ? "#c0d1ff" : "#ffc2a2";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(t.pos.x, t.pos.y, t.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#f58271";
      ctx.beginPath();
      ctx.arc(
        t.pos.x,
        t.pos.y,
        t.radius,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * (1 - t.remaining / t.duration),
      );
      ctx.stroke();
      if (t.label) {
        ctx.font = '600 13px "Microsoft YaHei UI",sans-serif';
        ctx.textAlign = "center";
        ctx.fillStyle = "#ffe3c1";
        ctx.fillText(t.label, t.pos.x, t.pos.y - t.radius - 12);
      }
      ctx.restore();
    }
  }

  private drawEffect(e: CombatEffect): void {
    const ctx = this.context,
      p = 1 - e.life / e.duration;
    ctx.save();
    ctx.translate(e.pos.x, e.pos.y - 12);
    ctx.globalAlpha = Math.min(1, (e.life / e.duration) * 2);
    ctx.strokeStyle = e.color;
    ctx.fillStyle = e.color;
    ctx.shadowColor = e.color;
    ctx.shadowBlur = 16;
    if (e.kind === "ring" || e.kind === "awaken") {
      const r = e.radius * (0.2 + p * 0.8);
      ctx.lineWidth = (1 - p) * 7 + 1;
      ctx.beginPath();
      ctx.ellipse(0, 0, r, r * 0.62, 0, 0, Math.PI * 2);
      ctx.stroke();
      for (let i = 0; i < 12; i++) {
        const a = (i * Math.PI) / 6 + p;
        ctx.beginPath();
        ctx.arc(
          Math.cos(a) * r,
          Math.sin(a) * r * 0.62 - (e.kind === "awaken" ? p * 80 : 0),
          3,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      if (e.kind === "awaken") {
        const g = ctx.createLinearGradient(0, -150, 0, 10);
        g.addColorStop(0, "transparent");
        g.addColorStop(1, "#ffeebd88");
        ctx.fillStyle = g;
        ctx.fillRect(-28, -150, 56, 155);
      }
    } else if (e.kind === "slash") {
      ctx.rotate(e.angle);
      ctx.lineWidth = 8 * (1 - p) + 1;
      ctx.beginPath();
      ctx.arc(0, 0, e.radius * (0.65 + p * 0.35), -0.8 + p, 0.8 + p);
      ctx.stroke();
    } else if (e.kind === "dash") {
      ctx.rotate(e.angle);
      ctx.lineWidth = 12 * (1 - p);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(e.radius, 0);
      ctx.stroke();
    } else {
      for (let i = 0; i < 7; i++) {
        const a = (i * Math.PI * 2) / 7;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * 8, Math.sin(a) * 8);
        ctx.lineTo(Math.cos(a) * e.radius * p, Math.sin(a) * e.radius * p);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawSky(snapshot: RenderSnapshot): void {
    const ctx = this.context;
    const sky = inkAsset("sky");
    if (!this.legacy && sky) {
      // The screen backdrop is cropped to the physical viewport aspect; never stretch clouds on portrait devices.
      const aspect = this.cssWidth / this.cssHeight,
        sw = Math.min(sky.width, sky.height * aspect),
        sh = sw / aspect;
      ctx.drawImage(
        sky,
        (sky.width - sw) / 2,
        (sky.height - sh) / 2,
        sw,
        sh,
        0,
        0,
        VIEW_WIDTH,
        VIEW_HEIGHT,
      );
      const night = Math.max(0, 0.35 - Math.sin(snapshot.dayTime * Math.PI));
      if (night > 0) {
        ctx.fillStyle = `rgba(51,66,84,${night * 0.6})`;
        ctx.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);
      }
      return;
    }
    const daylight = clamp(Math.sin(snapshot.dayTime * Math.PI), 0, 1);
    const top = this.mixColor([18, 36, 58], [107, 168, 177], daylight);
    const bottom = this.mixColor([51, 53, 70], [207, 222, 186], daylight);
    const gradient = ctx.createLinearGradient(0, 0, 0, VIEW_HEIGHT);
    gradient.addColorStop(0, top);
    gradient.addColorStop(0.66, bottom);
    gradient.addColorStop(
      1,
      this.mixColor([42, 60, 68], [147, 187, 167], daylight),
    );
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, VIEW_WIDTH, VIEW_HEIGHT);

    const orbit = snapshot.dayTime * Math.PI * 2 - Math.PI;
    const celestialX = VIEW_WIDTH * 0.5 + Math.cos(orbit) * 570;
    const celestialY = 590 - Math.sin(orbit) * 470;
    const glow = ctx.createRadialGradient(
      celestialX,
      celestialY,
      5,
      celestialX,
      celestialY,
      daylight > 0.1 ? 110 : 75,
    );
    glow.addColorStop(
      0,
      daylight > 0.1 ? "rgba(255,244,191,.92)" : "rgba(211,224,231,.88)",
    );
    glow.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(celestialX, celestialY, 120, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = daylight > 0.1 ? "#f9ecc0" : "#d9e4e8";
    ctx.beginPath();
    ctx.arc(celestialX, celestialY, daylight > 0.1 ? 30 : 23, 0, Math.PI * 2);
    ctx.fill();

    if (daylight < 0.42) {
      ctx.fillStyle = `rgba(235,244,225,${(0.42 - daylight) * 1.7})`;
      for (let i = 0; i < 44; i += 1) {
        const x = hashNoise(i * 3.7) * VIEW_WIDTH;
        const y = 35 + hashNoise(i * 7.1) * 410;
        const radius = 0.7 + hashNoise(i * 9.2) * 1.4;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let layer = 0; layer < 3; layer += 1) {
      const speed = 4 + layer * 2;
      const alpha = 0.06 + layer * 0.025;
      for (let index = 0; index < 5; index += 1) {
        const x =
          ((index * 390 + snapshot.time * speed + layer * 160) % 2100) - 260;
        const y = 130 + layer * 125 + Math.sin(index * 2.1 + layer) * 38;
        this.drawCloud(x, y, 0.7 + layer * 0.2, alpha);
      }
    }
  }

  private drawCloud(x: number, y: number, scale: number, alpha: number): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale * 0.55);
    ctx.fillStyle = `rgba(246,250,232,${alpha})`;
    ctx.beginPath();
    ctx.arc(0, 15, 54, 0, Math.PI * 2);
    ctx.arc(58, 0, 72, 0, Math.PI * 2);
    ctx.arc(126, 17, 52, 0, Math.PI * 2);
    ctx.arc(76, 30, 78, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawDistantIslands(time: number): void {
    const ctx = this.context;
    const islands = [
      { x: 160, y: 385, s: 0.42, phase: 0.4 },
      { x: 1440, y: 345, s: 0.34, phase: 1.8 },
      { x: 1330, y: 680, s: 0.2, phase: 2.7 },
    ];
    for (const island of islands) {
      const bob = Math.sin(time * 0.35 + island.phase) * 5;
      ctx.save();
      ctx.translate(island.x, island.y + bob);
      ctx.scale(island.s, island.s);
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = "#354d4b";
      ctx.beginPath();
      ctx.ellipse(0, 0, 190, 72, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#29383a";
      ctx.beginPath();
      ctx.moveTo(-160, 12);
      ctx.quadraticCurveTo(-30, 260, 0, 300);
      ctx.quadraticCurveTo(58, 220, 165, 10);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  }

  private drawIsland(time: number, dayTime: number): void {
    const ctx = this.context;
    const bob = Math.sin(time * 0.38) * 3;
    ctx.save();
    ctx.translate(800, 485 + bob);

    ctx.fillStyle = "rgba(11,24,31,.22)";
    ctx.beginPath();
    ctx.ellipse(0, 292, 470, 64, 0, 0, Math.PI * 2);
    ctx.fill();

    const underside = ctx.createLinearGradient(0, 60, 0, 380);
    underside.addColorStop(0, "#5a6750");
    underside.addColorStop(0.3, "#3d4a43");
    underside.addColorStop(1, "#202f35");
    ctx.fillStyle = underside;
    ctx.beginPath();
    ctx.moveTo(-545, 18);
    ctx.bezierCurveTo(-430, 110, -285, 208, -130, 316);
    ctx.bezierCurveTo(-50, 370, 25, 390, 82, 328);
    ctx.bezierCurveTo(185, 220, 420, 124, 546, 10);
    ctx.quadraticCurveTo(340, 122, 0, 142);
    ctx.quadraticCurveTo(-350, 110, -545, 18);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = "rgba(190,202,161,.11)";
    ctx.lineWidth = 3;
    for (let i = 0; i < 12; i += 1) {
      const x = -420 + i * 76;
      ctx.beginPath();
      ctx.moveTo(x, 72 + Math.sin(i) * 15);
      ctx.quadraticCurveTo(
        x + 20,
        150 + (i % 3) * 42,
        x + 55,
        222 + (i % 4) * 18,
      );
      ctx.stroke();
    }

    const grass = ctx.createRadialGradient(-120, -70, 20, 0, 0, 610);
    grass.addColorStop(0, "#7fa568");
    grass.addColorStop(0.58, "#60845f");
    grass.addColorStop(1, "#405d50");
    ctx.fillStyle = grass;
    ctx.beginPath();
    ctx.ellipse(0, 0, 570, 315, -0.015, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(213,232,170,.23)";
    ctx.lineWidth = 8;
    ctx.stroke();

    const path = ctx.createRadialGradient(10, 0, 0, 10, 0, 280);
    path.addColorStop(0, "rgba(213,202,148,.28)");
    path.addColorStop(1, "rgba(193,182,127,0)");
    ctx.fillStyle = path;
    ctx.beginPath();
    ctx.ellipse(40, 15, 310, 170, -0.08, 0, Math.PI * 2);
    ctx.fill();

    const rimLight = clamp(Math.sin(dayTime * Math.PI), 0, 1);
    ctx.strokeStyle = `rgba(233,244,187,${0.08 + rimLight * 0.12})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(-20, -4, 545, 292, -0.015, Math.PI * 1.05, Math.PI * 1.9);
    ctx.stroke();
    ctx.restore();
  }

  private drawWaterGarden(time: number): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(800, 485 + Math.sin(time * 0.38) * 3);
    const water = ctx.createRadialGradient(-250, 30, 10, -250, 30, 100);
    water.addColorStop(0, "rgba(164,218,210,.72)");
    water.addColorStop(1, "rgba(57,130,125,.74)");
    ctx.fillStyle = water;
    ctx.beginPath();
    ctx.ellipse(-278, 33, 112, 66, -0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(229,246,220,.24)";
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i += 1) {
      ctx.beginPath();
      ctx.ellipse(
        -295 + i * 21,
        24 + Math.sin(time + i) * 3,
        24 + i * 3,
        7,
        -0.1,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
    }
    ctx.fillStyle = "#e4d987";
    ctx.beginPath();
    ctx.arc(-235, 17, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawPlot(
    plot: FarmPlot,
    time: number,
    selected: boolean,
    near = false,
  ): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(plot.pos.x, plot.pos.y);
    if (selected) {
      ctx.strokeStyle = "rgba(238,244,177,.86)";
      ctx.lineWidth = 2;
      ctx.setLineDash([7, 6]);
      ctx.beginPath();
      ctx.ellipse(0, 1, 57, 34, -0.07, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const inkBed = !this.legacy && inkAsset("props/bed");
    if (inkBed) inkPart(ctx, "props/bed", 0, 25, 76);
    else {
      ctx.fillStyle = "rgba(24,34,29,.24)";
      ctx.beginPath();
      ctx.ellipse(4, 9, 51, 28, 0, 0, Math.PI * 2);
      ctx.fill();
      const soil = ctx.createRadialGradient(-8, -8, 2, 0, 0, 52);
      soil.addColorStop(0, plot.moisture > 0.45 ? "#725942" : "#84694d");
      soil.addColorStop(1, plot.moisture > 0.45 ? "#4a4437" : "#62513d");
      ctx.fillStyle = soil;
      ctx.beginPath();
      ctx.ellipse(0, 0, 49, 26, -0.07, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = `rgba(170,207,130,${0.18 + plot.fertility * 0.2})`;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.strokeStyle = "rgba(32,35,29,.24)";
      ctx.lineWidth = 1;
      for (let row = -1; row <= 1; row += 1) {
        ctx.beginPath();
        ctx.moveTo(-38, row * 7);
        ctx.quadraticCurveTo(0, row * 4 - 3, 38, row * 7);
        ctx.stroke();
      }
    }
    if (plot.cropId) this.drawCrop(plot, time);
    const gardenPlot = plot as GardenPlot;
    if (gardenPlot.sprinkler) {
      ctx.strokeStyle = "#b4e5e1";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(40, 6);
      ctx.lineTo(40, -14);
      ctx.stroke();
      ctx.fillStyle = "#d0e9d0";
      ctx.beginPath();
      ctx.arc(40, -14, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    if (
      (near || selected) &&
      (plot.stage === "mature" || (plot.cropId && plot.moisture < 0.2))
    ) {
      ctx.fillStyle = plot.stage === "mature" ? "#715930" : "#386c7a";
      ctx.font = "bold 14px sans-serif";
      ctx.textAlign = "center";
      ctx.shadowColor = "#fff1cd";
      ctx.shadowBlur = 3;
      ctx.fillText(
        plot.stage === "mature" ? "✦ 可唤醒 / 收获" : "◉ 缺水",
        0,
        -98,
      );
      ctx.shadowBlur = 0;
    }
    ctx.restore();
  }

  private drawCrop(plot: FarmPlot, time: number): void {
    if (!plot.cropId) return;
    const ctx = this.context;
    const crop = CROPS[plot.cropId];
    const progress = clamp(plot.growth, 0.08, 1);
    const sway = Math.sin(time * 1.7 + plot.id) * (1.4 + progress * 2);
    const scale = 0.45 + progress * 0.64;
    ctx.save();
    ctx.translate(0, -10);
    if (this.legacy || !inkAsset(`v8/crop-${plot.cropId}/mature`))
      ctx.scale(scale, scale);

    if (
      (plot.growth >= 0.2 ||
        (!this.legacy && inkAsset(`v8/crop-${plot.cropId}/mature`))) &&
      this.art.animated(
        ctx,
        `crop-${plot.cropId}`,
        { ...plot, motion: { id: "grow", age: progress, duration: 1 } },
        time + plot.id,
        95,
        95,
        false,
        true,
      )
    ) {
      if (plot.stage === "mature") {
        ctx.strokeStyle = "rgba(252,232,146,.6)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(0, 4, 40 + Math.sin(time * 2) * 3, 15, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    if (plot.stage === "seed") {
      ctx.fillStyle = "#a6c578";
      ctx.beginPath();
      ctx.ellipse(0, 4, 5, 9, -0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    ctx.strokeStyle = "#436a42";
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(0, 12);
    ctx.quadraticCurveTo(sway * 0.5, -14, sway, -31);
    ctx.stroke();
    this.drawLeaf(-11, -12, -0.45, crop.primary);
    if (plot.stage !== "sprout") this.drawLeaf(12, -22, 0.5, crop.secondary);

    if (plot.stage === "flower") {
      ctx.fillStyle = crop.secondary;
      for (let i = 0; i < 5; i += 1) {
        ctx.save();
        ctx.rotate(i * Math.PI * 0.4);
        ctx.beginPath();
        ctx.ellipse(0, -37, 5, 10, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    if (plot.stage === "mature")
      this.drawMatureProduce(
        plot.cropId,
        crop.primary,
        crop.secondary,
        time + plot.id,
      );
    ctx.restore();
  }

  private drawMatureProduce(
    cropId: keyof typeof CROPS,
    primary: string,
    secondary: string,
    time: number,
  ): void {
    const ctx = this.context;
    if (cropId === "pumpkin") {
      ctx.fillStyle = primary;
      ctx.beginPath();
      ctx.ellipse(0, -2, 25, 19, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(125,65,30,.38)";
      ctx.lineWidth = 2;
      for (const x of [-12, 0, 12]) {
        ctx.beginPath();
        ctx.ellipse(x, -2, 8, 18, 0, -1.3, 1.3);
        ctx.stroke();
      }
    } else if (cropId === "coconut") {
      ctx.strokeStyle = "#7d6245";
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.moveTo(0, 10);
      ctx.quadraticCurveTo(-5, -35, 2, -65);
      ctx.stroke();
      for (let i = 0; i < 5; i += 1)
        this.drawLeaf(
          Math.cos(i * 1.25) * 30,
          -63 + Math.sin(i * 1.25) * 9,
          i * 1.25,
          primary,
          1.35,
        );
      ctx.fillStyle = "#806541";
      for (let i = 0; i < 3; i += 1) {
        ctx.beginPath();
        ctx.arc(-7 + i * 7, -48 + (i % 2) * 6, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (cropId === "wintermelon") {
      ctx.fillStyle = primary;
      ctx.beginPath();
      ctx.ellipse(0, -4, 19, 29, -0.15, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(216,239,202,.45)";
      ctx.lineWidth = 2;
      for (const x of [-9, 0, 9]) {
        ctx.beginPath();
        ctx.moveTo(x, -28);
        ctx.lineTo(x - 2, 20);
        ctx.stroke();
      }
    } else if (cropId === "banana") {
      for (let i = 0; i < 4; i += 1)
        this.drawLeaf(
          Math.cos(i * 1.45) * 21,
          -43,
          i * 1.45 - 1.4,
          primary,
          1.35,
        );
      ctx.strokeStyle = secondary;
      ctx.lineWidth = 7;
      ctx.lineCap = "round";
      for (let i = 0; i < 5; i += 1) {
        ctx.beginPath();
        ctx.arc(8 + i * 3, -22 + i * 3, 15, 1.4, 2.7);
        ctx.stroke();
      }
    } else {
      const glow = ctx.createRadialGradient(0, -32, 2, 0, -32, 42);
      glow.addColorStop(0, "rgba(217,204,255,.82)");
      glow.addColorStop(1, "rgba(168,150,216,0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(0, -32, 42, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = secondary;
      for (let i = 0; i < 6; i += 1) {
        ctx.save();
        ctx.translate(0, -32);
        ctx.rotate(time * 0.08 + (i * Math.PI) / 3);
        ctx.beginPath();
        ctx.ellipse(0, -13, 5, 13, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }

  private drawLeaf(
    x: number,
    y: number,
    rotation: number,
    color: string,
    scale = 1,
  ): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.bezierCurveTo(-9, -6, -10, -20, 0, -25);
    ctx.bezierCurveTo(10, -18, 11, -7, 0, 0);
    ctx.fill();
    ctx.strokeStyle = "rgba(244,250,220,.22)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -2);
    ctx.lineTo(0, -20);
    ctx.stroke();
    ctx.restore();
  }

  private drawResourceNode(
    node: ResourceNode,
    time: number,
    near = false,
  ): void {
    const ctx = this.context;
    const healthRatio = node.health / node.maxHealth;
    ctx.save();
    ctx.translate(node.pos.x, node.pos.y);
    if (
      this.art.animated(
        ctx,
        node.kind,
        node,
        time + node.id,
        node.kind === "tree" ? 145 : 99,
        node.kind === "tree" ? 160 : 92,
        false,
        true,
      )
    ) {
      if (healthRatio < 1) this.drawHealthBar(healthRatio, -100, "#b9deaa");
      if (near && node.kind !== "tree" && node.kind !== "rock") {
        ctx.font = '600 11px "Microsoft YaHei UI"';
        ctx.textAlign = "center";
        ctx.fillStyle = this.legacy ? "#f4e9c8" : "#4b5037";
        ctx.fillText(RESOURCES[node.kind].name, 0, 17);
      }
      ctx.restore();
      return;
    }
    ctx.fillStyle = "rgba(20,31,27,.22)";
    ctx.beginPath();
    ctx.ellipse(3, 9, node.radius * 0.9, node.radius * 0.36, 0, 0, Math.PI * 2);
    ctx.fill();
    if (node.kind === "tree") {
      const sway = Math.sin(time * 1.1 + node.sway) * 0.035;
      ctx.rotate(sway);
      ctx.fillStyle = "#705944";
      ctx.beginPath();
      ctx.roundRect(-9, -45, 18, 58, 7);
      ctx.fill();
      const foliage = ["#466d50", "#5f8a58", "#789d61"];
      for (let i = 0; i < 9; i += 1) {
        const angle = i * 2.4;
        const x = Math.cos(angle) * (18 + (i % 3) * 9);
        const y = -58 + Math.sin(angle) * 19 - (i % 2) * 9;
        ctx.fillStyle = foliage[i % foliage.length];
        ctx.beginPath();
        ctx.arc(x, y, 22 + (i % 3) * 3, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      const stone = ctx.createLinearGradient(-30, -30, 30, 20);
      stone.addColorStop(0, "#9ba293");
      stone.addColorStop(1, "#59685f");
      ctx.fillStyle = stone;
      ctx.beginPath();
      ctx.moveTo(-31, 9);
      ctx.lineTo(-25, -16);
      ctx.lineTo(-7, -34);
      ctx.lineTo(20, -27);
      ctx.lineTo(34, -4);
      ctx.lineTo(23, 17);
      ctx.lineTo(-13, 20);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "rgba(235,242,221,.2)";
      ctx.beginPath();
      ctx.moveTo(-7, -31);
      ctx.lineTo(2, -4);
      ctx.lineTo(31, -4);
      ctx.stroke();
    }
    if (healthRatio < 1) {
      ctx.fillStyle = "rgba(7,15,16,.5)";
      ctx.fillRect(-26, -88, 52, 4);
      ctx.fillStyle = "#d7dc8a";
      ctx.fillRect(-26, -88, 52 * healthRatio, 4);
    }
    ctx.restore();
  }

  private drawGroundDetails(time: number): void {
    const ctx = this.context;
    ctx.save();
    ctx.fillStyle = "rgba(226,239,176,.42)";
    for (let i = 0; i < 64; i += 1) {
      const angle = hashNoise(i * 4.1) * Math.PI * 2;
      const radius = Math.sqrt(hashNoise(i * 6.7)) * 520;
      const x = 800 + Math.cos(angle) * radius;
      const y = 485 + Math.sin(angle) * radius * 0.5;
      if (
        (x > 455 && x < 920 && y > 300 && y < 560) ||
        (x < 420 && y > 420 && y < 580)
      )
        continue;
      ctx.save();
      ctx.translate(x, y);
      if (i % 6 === 0)
        this.art.animated(ctx, "herb", {}, time + i, 28, 32, false, true);
      else {
        const phase = Math.floor(time * 8 + i) / 8;
        ctx.strokeStyle = "rgba(196,219,144,.65)";
        ctx.lineWidth = 1.7;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(
          Math.sin(phase) * 3,
          -5,
          Math.sin(phase) * 4 + 3,
          -10,
        );
        ctx.stroke();
      }
      ctx.restore();
      const flicker = 0.45 + Math.sin(time * 2 + i) * 0.25;
      ctx.globalAlpha = flicker;
      ctx.beginPath();
      ctx.arc(x, y, 1.2 + (i % 3) * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawPlayer(
    player: Player,
    time: number,
    spiritAbsent: boolean,
  ): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(player.pos.x, player.pos.y);
    if (spiritAbsent) ctx.globalAlpha = 0.38;
    this.drawActorShadow(player.radius);
    if (
      this.art.animated(
        ctx,
        "hero",
        player,
        time,
        displayMetrics("hero").width,
        displayMetrics("hero").height,
        Math.cos(player.facing) < 0,
      )
    ) {
      if (player.flash > 0) {
        ctx.strokeStyle = "#fff5ce";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(0, -40, 32, 44, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (player.attackCooldown > 0.17)
        this.drawAttackArc(player.facing, 65, "#b5ffe4");
      this.drawHealthBar(player.health / player.maxHealth, -104, "#a8edbb");
      ctx.restore();
      return;
    }
    ctx.rotate(player.facing * 0.03);
    ctx.fillStyle = player.flash > 0 ? "#fff5d2" : "#325d58";
    ctx.beginPath();
    ctx.ellipse(0, -19, 23, 30, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#d9c7a2";
    ctx.beginPath();
    ctx.arc(0, -45, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#273f3f";
    ctx.beginPath();
    ctx.arc(-4, -50, 16, Math.PI, Math.PI * 2);
    ctx.lineTo(18, -39);
    ctx.quadraticCurveTo(5, -43, -10, -40);
    ctx.fill();
    ctx.strokeStyle = "#8e7656";
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(17, -20);
    ctx.lineTo(31, -62);
    ctx.stroke();
    ctx.fillStyle = "#c6da86";
    ctx.beginPath();
    ctx.arc(33, -67, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(213,239,169,.5)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(33, -67, 12 + Math.sin(time * 3) * 2, 0, Math.PI * 2);
    ctx.stroke();
    if (player.attackCooldown > 0.21)
      this.drawAttackArc(player.facing, 58, "#dff0a8");
    this.drawHealthBar(player.health / player.maxHealth, -54, "#a8db7d");
    ctx.restore();
  }

  private drawCompanion(
    companion: Companion,
    time: number,
    selected: boolean,
  ): void {
    const ctx = this.context;
    const crop = CROPS[companion.cropId];
    ctx.save();
    ctx.translate(companion.pos.x, companion.pos.y);
    this.drawActorShadow(
      Math.max(companion.radius, displayMetrics(companion.cropId).width * 0.24),
    );
    if (selected || companion.possessed) {
      ctx.strokeStyle = companion.possessed
        ? "rgba(239,222,143,.95)"
        : "rgba(203,232,145,.72)";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.ellipse(
        0,
        4,
        companion.radius + 12,
        (companion.radius + 12) * 0.42,
        0,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (
      this.art.animated(
        ctx,
        companion.cropId,
        companion,
        time + companion.id,
        displayMetrics(companion.cropId).width,
        displayMetrics(companion.cropId).height,
        Math.cos(companion.facing) < 0,
      )
    ) {
      if (companion.attackCooldown > 0.35)
        this.drawAttackArc(companion.facing, 60, crop.secondary);
      this.drawHealthBar(
        companion.health / companion.maxHealth,
        -displayMetrics(companion.cropId).height * 0.83,
        crop.secondary,
      );
      ctx.restore();
      return;
    }
    ctx.fillStyle = companion.flash > 0 ? "#fff5dc" : crop.primary;
    if (companion.cropId === "pumpkin") {
      ctx.beginPath();
      ctx.ellipse(0, -19, 25, 23, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#765030";
      ctx.lineWidth = 2;
      for (const x of [-10, 0, 10]) {
        ctx.beginPath();
        ctx.moveTo(x, -39);
        ctx.lineTo(x, 1);
        ctx.stroke();
      }
      this.drawFace(0, -20);
      ctx.strokeStyle = "#70543c";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(18, -11);
      ctx.lineTo(37, -36);
      ctx.stroke();
      ctx.fillStyle = "#9ca8a0";
      ctx.beginPath();
      ctx.moveTo(31, -44);
      ctx.lineTo(47, -39);
      ctx.lineTo(42, -27);
      ctx.lineTo(28, -33);
      ctx.closePath();
      ctx.fill();
    } else if (companion.cropId === "coconut") {
      ctx.fillStyle = "#876a45";
      ctx.beginPath();
      ctx.roundRect(-15, -47, 30, 49, 13);
      ctx.fill();
      this.drawFace(0, -28);
      for (let i = 0; i < 5; i += 1)
        this.drawLeaf(
          Math.cos(i * 1.25) * 10,
          -46,
          i * 1.25 - 0.6,
          crop.primary,
          0.9,
        );
    } else if (companion.cropId === "wintermelon") {
      ctx.beginPath();
      ctx.ellipse(0, -24, 23, 34, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(223,240,210,.42)";
      ctx.lineWidth = 2;
      for (const x of [-10, 0, 10]) {
        ctx.beginPath();
        ctx.moveTo(x, -54);
        ctx.lineTo(x, 5);
        ctx.stroke();
      }
      this.drawFace(0, -27);
    } else if (companion.cropId === "banana") {
      ctx.fillStyle = "#667c43";
      ctx.beginPath();
      ctx.roundRect(-12, -40, 24, 43, 10);
      ctx.fill();
      this.drawFace(0, -23);
      this.drawLeaf(-10, -40, -1.05, crop.primary, 1.1);
      this.drawLeaf(10, -42, 0.9, crop.secondary, 1.1);
    } else {
      ctx.fillStyle = "#6d648e";
      ctx.beginPath();
      ctx.ellipse(0, -19, 21, 25, 0, 0, Math.PI * 2);
      ctx.fill();
      this.drawFace(0, -20);
      for (let i = 0; i < 6; i += 1)
        this.drawLeaf(
          Math.cos(i) * 6,
          -39,
          (i * Math.PI) / 3,
          crop.secondary,
          0.75,
        );
      ctx.strokeStyle = `rgba(214,202,255,${0.35 + Math.sin(time * 2) * 0.15})`;
      ctx.beginPath();
      ctx.arc(0, -24, 37, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (companion.attackCooldown > 0.35)
      this.drawAttackArc(
        companion.facing,
        companion.radius + 24,
        crop.secondary,
      );
    this.drawHealthBar(
      companion.health / companion.maxHealth,
      -63,
      crop.secondary,
    );
    ctx.restore();
  }

  private drawEnemy(enemy: Enemy, time: number): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(enemy.pos.x, enemy.pos.y);
    this.drawActorShadow(enemy.radius);
    if (enemy.boss) {
      this.expeditionArt.bossAccent(ctx, enemy, time, this.compactCombat);
      if (!this.legacy) {
        this.art.animated(
          ctx,
          enemy.boss.id,
          enemy,
          time,
          bossDrawSize(this.compactCombat),
          bossDrawSize(this.compactCombat),
        );
        ctx.restore();
        return;
      }
      if (
        this.expeditionArt.boss(
          ctx,
          enemy.boss.id,
          enemy,
          time,
          enemy.facing,
          bossDrawSize(this.compactCombat),
        )
      ) {
        ctx.restore();
        return;
      }
      // 素材尚未就绪时保持可见的普通敌人回退，逻辑与预警不依赖图片加载。
    }
    const size = this.legacy
      ? enemy.kind === "brute"
        ? enemy.elite
          ? 144
          : 123
        : 96
      : (displayMetrics(enemy.kind).width / 1.15) * (enemy.elite ? 1.12 : 1);
    if (
      this.art.animated(
        ctx,
        enemy.kind,
        enemy,
        time + enemy.id,
        size * 1.15,
        size * 1.15,
        Math.cos(enemy.facing) < 0,
      )
    ) {
      this.drawHealthBar(
        enemy.health / enemy.maxHealth,
        -size,
        ENEMIES[enemy.kind].color,
      );
      ctx.font = '600 12px "Microsoft YaHei UI",sans-serif';
      ctx.textAlign = "center";
      ctx.fillStyle = (enemy.recovery ?? 0) > 0 ? "#fff1a7" : "#ffe6db";
      ctx.shadowColor = "#211b32";
      ctx.shadowBlur = 4;
      ctx.fillText(
        (enemy.recovery ?? 0) > 0
          ? "破绽 · 伤害 +40%"
          : (enemy.elite ? "精英 · " : "") + ENEMIES[enemy.kind].name,
        0,
        -size - 9,
      );
      ctx.restore();
      return;
    }
    const colors =
      enemy.kind === "brute"
        ? ["#684b59", "#b06569"]
        : enemy.kind === "spitter"
          ? ["#4a5862", "#8ea47e"]
          : ["#553f58", "#956277"];
    ctx.fillStyle = enemy.flash > 0 ? "#ffe8dc" : colors[0];
    ctx.beginPath();
    if (enemy.kind === "brute") ctx.ellipse(0, -20, 33, 32, 0, 0, Math.PI * 2);
    else ctx.ellipse(0, -17, 22, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = colors[1];
    for (let i = 0; i < (enemy.kind === "brute" ? 6 : 4); i += 1) {
      ctx.save();
      ctx.rotate((i * Math.PI) / 3 + time * 0.08);
      ctx.beginPath();
      ctx.moveTo(0, -18);
      ctx.lineTo(5, -34 - (i % 2) * 8);
      ctx.lineTo(-5, -31);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    ctx.fillStyle = "#f1b37f";
    ctx.beginPath();
    ctx.arc(-7, -20, 3, 0, Math.PI * 2);
    ctx.arc(7, -20, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#281f27";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, -12, 7, 0.2, Math.PI - 0.2);
    ctx.stroke();
    this.drawHealthBar(
      enemy.health / enemy.maxHealth,
      enemy.kind === "brute" ? -66 : -50,
      "#e87870",
    );
    ctx.restore();
  }

  private drawActorShadow(radius: number): void {
    const ctx = this.context;
    ctx.fillStyle = "rgba(12,23,22,.25)";
    ctx.beginPath();
    ctx.ellipse(3, 5, radius * 1.1, radius * 0.42, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawFace(x: number, y: number): void {
    const ctx = this.context;
    ctx.fillStyle = "#243329";
    ctx.beginPath();
    ctx.arc(x - 7, y - 2, 2.3, 0, Math.PI * 2);
    ctx.arc(x + 7, y - 2, 2.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#344435";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y + 2, 6, 0.3, Math.PI - 0.3);
    ctx.stroke();
  }

  private drawHealthBar(ratio: number, y: number, color: string): void {
    if (ratio >= 0.999) return;
    const ctx = this.context;
    ctx.fillStyle = "rgba(7,13,16,.54)";
    ctx.fillRect(-24, y, 48, 4);
    ctx.fillStyle = color;
    ctx.fillRect(-24, y, 48 * clamp(ratio, 0, 1), 4);
  }

  private drawAttackArc(facing: number, radius: number, color: string): void {
    const ctx = this.context;
    ctx.save();
    ctx.rotate(facing);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.62;
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.arc(0, -15, radius, -0.72, 0.72);
    ctx.stroke();
    ctx.restore();
  }

  private drawParticle(particle: Particle): void {
    const ctx = this.context;
    const alpha = clamp(particle.life / particle.maxLife, 0, 1);
    ctx.save();
    ctx.translate(particle.pos.x, particle.pos.y);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = particle.color;
    ctx.strokeStyle = particle.color;
    if (particle.shape === "leaf") {
      ctx.rotate(Math.atan2(particle.velocity.y, particle.velocity.x));
      ctx.beginPath();
      ctx.ellipse(
        0,
        0,
        particle.size * 1.7,
        particle.size,
        0.3,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    } else if (particle.shape === "spark") {
      ctx.lineWidth = Math.max(1, particle.size * 0.35);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(-particle.velocity.x * 0.05, -particle.velocity.y * 0.05);
      ctx.stroke();
    } else if (particle.shape === "ring") {
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, particle.size * (2 - alpha), 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(0, 0, particle.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawFloatingText(text: FloatingText): void {
    const ctx = this.context;
    ctx.save();
    ctx.globalAlpha = clamp(text.life / 1.1, 0, 1);
    ctx.fillStyle = text.color;
    ctx.font = '700 13px "Microsoft YaHei UI", sans-serif';
    ctx.textAlign = "center";
    ctx.shadowColor = "rgba(6,12,15,.65)";
    ctx.shadowBlur = 4;
    ctx.fillText(text.text, text.pos.x, text.pos.y);
    ctx.restore();
  }

  private drawPointer(pointer: Vec2, time: number): void {
    const ctx = this.context;
    ctx.save();
    ctx.translate(pointer.x, pointer.y);
    ctx.rotate(time * 0.8);
    ctx.strokeStyle = "rgba(231,243,196,.48)";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 5]);
    ctx.beginPath();
    ctx.arc(0, 0, 10 + Math.sin(time * 4) * 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  private drawWeather(snapshot: RenderSnapshot): void {
    if (snapshot.weather === "clear") return;
    const ctx = this.context;
    const count = snapshot.weather === "rain" ? 120 : 48;
    ctx.save();
    ctx.strokeStyle =
      snapshot.weather === "rain"
        ? "rgba(204,232,229,.28)"
        : "rgba(221,239,231,.18)";
    ctx.lineWidth = 1;
    for (let i = 0; i < count; i += 1) {
      const x =
        (hashNoise(i * 8.41) * VIEW_WIDTH + snapshot.time * 95) % VIEW_WIDTH;
      const y =
        (hashNoise(i * 3.17) * VIEW_HEIGHT +
          snapshot.time * (290 + (i % 5) * 16)) %
        VIEW_HEIGHT;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - 8, y + (snapshot.weather === "rain" ? 24 : 15));
      ctx.stroke();
    }
    ctx.restore();
  }

  private mixColor(
    a: [number, number, number],
    b: [number, number, number],
    amount: number,
  ): string {
    return `rgb(${Math.round(lerp(a[0], b[0], amount))}, ${Math.round(lerp(a[1], b[1], amount))}, ${Math.round(lerp(a[2], b[2], amount))})`;
  }
}
